import {
  differenceInDays,
  differenceInHours,
  differenceInSeconds,
  differenceInYears,
  format,
  formatDistanceToNowStrict,
  isToday,
  parse,
  parseISO,
} from 'date-fns';

import type { Timestamp } from 'src/types/utilityTypes';

export function formatTime(timestamp: Timestamp | Date): string {
  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  return new Date(time).toLocaleTimeString(undefined, {
    timeStyle: 'short',
  });
}

export function differenceFromNowInSeconds(
  timestamp: Timestamp | Date
): number {
  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  return differenceInSeconds(new Date(), new Date(time));
}

export function formatTimeAgo(timestamp: Timestamp | Date): string {
  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  return formatDistanceToNowStrict(new Date(time), { addSuffix: true });
}

export function formatRelativeTime(timestamp: Timestamp | Date): string {
  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  const date = new Date(time);
  const now = new Date();

  // Calculate time differences
  const hoursAgo = differenceInHours(now, date);
  const daysAgo = differenceInDays(now, date);
  const yearsAgo = differenceInYears(now, date);

  // Today: just show time (e.g., "4:01 pm")
  if (isToday(date)) {
    return format(date, 'p');
  }

  // Yesterday to 48 hours: hours counter (e.g., "22h")
  if (hoursAgo < 48) {
    return `${hoursAgo}h`;
  }

  // 48 hours to 1 week: days counter (e.g., "3d")
  if (daysAgo < 7) {
    return `${daysAgo}d`;
  }

  // Within this year: date without year (e.g., "Feb 20")
  if (yearsAgo < 1) {
    return format(date, 'MMM d');
  }

  // Previous years: date with year (e.g., "Feb 20, 2022")
  return format(date, 'MMM d, yyyy');
}

export function formatShortTimeAgo(timestamp: Timestamp | Date): string {
  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  const formatDistanceLocale = {
    lessThanXSeconds: '{{count}}s',
    xSeconds: '{{count}}s',
    halfAMinute: '30s',
    lessThanXMinutes: '{{count}}m',
    xMinutes: '{{count}}m',
    aboutXHours: '{{count}}h',
    xHours: '{{count}}h',
    xDays: '{{count}}d',
    aboutXWeeks: '{{count}}w',
    xWeeks: '{{count}}w',
    aboutXMonths: '{{count}}Mo',
    xMonths: '{{count}}Mo',
    aboutXYears: '{{count}}y',
    xYears: '{{count}}y',
    overXYears: '{{count}}y',
    almostXYears: '{{count}}y',
  };

  function formatDistance(token, count, options) {
    options = options || {};
    const result = formatDistanceLocale[token].replace('{{count}}', count);

    if (options.addSuffix) {
      if (options.comparison > 0) {
        return 'in ' + result;
      } else {
        return result + ' ago';
      }
    }

    return result;
  }

  return formatDistanceToNowStrict(new Date(time), {
    addSuffix: false,
    locale: {
      formatDistance,
    },
  });
}

// NOTE: Need to consider i18n with date formatting using date-fns

export function formatListDateAndTime(
  date: Timestamp | Date | undefined | null
): string {
  if (!date) return '';

  date = timestampToDate(date);

  const timePart = format(date, 'p'); // Formats time as "9:30 AM" or "10:30 PM"

  if (isToday(date)) {
    return `Today at ${timePart}`;
  } else {
    return `${format(date, 'E MMM d')} at ${timePart}`;
  }
}

export function formatListDateAndTimeUTC(
  date: Timestamp | Date | undefined | null
): string {
  if (!date) return '';

  date = timestampToDate(date);

  // Convert to UTC
  // 60000 = 60 seconds * 1000 milliseconds, to convert minutes to milliseconds
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

  const timePart = format(utcDate, 'p'); // Formats time as "9:30 AM" or "10:30 PM"

  if (isToday(utcDate)) {
    return `Today at ${timePart} UTC`;
  } else {
    return `${format(utcDate, 'MMM d')} at ${timePart} UTC`;
  }
}

export function formatStartAndEndTime(
  start: Date | undefined | null,
  end: Date | undefined | null
): string {
  let formatted = '';
  if (start) {
    start = timestampToDate(start);
    formatted += format(start, 'h:mm a');
  }
  if (start && end) {
    formatted += ' - ';
  }
  if (end) {
    end = timestampToDate(end);
    formatted += format(end, 'h:mm a');
  }

  return formatted;
}

export function formatISODate(date: Date | undefined | null): string {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
}

export function formatFullDateAndTime(
  timestamp: Timestamp | Date | undefined | null,
  format?: Intl.DateTimeFormatOptions
): string {
  if (!timestamp) return '';

  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }

  return new Date(time).toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    ...format,
  });
}

export function formatFullDate(
  timestamp: Timestamp | Date | undefined | null,
  format?: Intl.DateTimeFormatOptions
): string {
  if (!timestamp) return '';

  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  return new Date(time).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...format,
  });
}

export function formatMonthDayYear(
  timestamp: Timestamp | Date | undefined | null,
  formatter?: string
) {
  if (!timestamp) return '';

  let formatString: string = formatter || 'MMM d, yyyy';
  if (typeof formatter !== 'string') {
    formatString = 'MMM d, yyyy';
  }

  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }

  return format(new Date(time), formatString);
}

export function timestampToDate(timestamp: Timestamp | Date): Date {
  return new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
}

export function dateToTimestamp(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

export function formatShortDate(
  timestamp: Timestamp | Date | undefined | null
): string {
  if (!timestamp) return '';

  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  return format(new Date(time), 'MMM d');
}

export function formatYearMonthDay(
  timestamp: Timestamp | Date | undefined | null,
  formatter?: string
): string {
  if (!timestamp) return '';

  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  return format(new Date(time), formatter || 'yyyy-MM-dd');
}

export function formatISODateToADF(timestamp: string | Date | null): string {
  if (!timestamp) return '';

  let date = timestamp; // Parse the input string
  if (typeof timestamp === 'string') {
    date = parseISO(timestamp);
  }
  return format(new Date(date), 'MM/dd/yy'); // Format the date to the desired format
}

export function format12Hour(time24: string | Date | null): string {
  if (!time24) return '';

  let date = time24;
  if (typeof time24 === 'string') {
    date = parse(time24, 'HH:mm', new Date());
  }
  return format(new Date(date), 'hh:mm a'); // 'hh' for 12-hour, 'mm' for minutes, 'a' for AM/PM
}

export function format24Hour(time12: string | Date | null): string {
  if (!time12) return '';

  let date = time12;
  if (typeof time12 === 'string') {
    // Try parsing as 12-hour format with AM/PM
    date = parse(time12, 'hh:mm a', new Date());
  }
  return format(new Date(date), 'HH:mm'); // 'HH' for 24-hour, 'mm' for minutes
}

export const formatSecondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formatTwoDigits = (num: number): string =>
    num.toString().padStart(2, '0');

  return `${formatTwoDigits(hours)}:${formatTwoDigits(
    minutes
  )}:${formatTwoDigits(remainingSeconds)}`;
};

export const formatMonthDayYearHyphenated = (
  timestamp: Timestamp | Date | undefined | null
): string => {
  if (!timestamp) return '';

  let time = timestamp;
  if (typeof timestamp === 'number') {
    time = timestamp * 1000;
  }
  return format(new Date(time), 'MM-dd-yyyy');
}